import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";
import PipelineStats from "@/pages/admin/contact/PipelineStats";
import StatusFilter from "@/pages/admin/contact/StatusFilter";
import ContactRow from "@/pages/admin/contact/ContactRow";

/**
 * CRM panel — orchestrates the contact-requests list, filters, pipeline
 * stats and follow-up automation. Heavy UI bits live in `./contact/*` so
 * this file stays focused on state + API plumbing.
 */
const ContactRequestsPanel = () => {
  const { authedAxios } = useAuth();
  const { lang, t } = useLanguage();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  // Local draft state for notes (so the textarea is responsive). Persisted on click.
  const [notesDraft, setNotesDraft] = useState({});
  const [savingNote, setSavingNote] = useState(null);
  const [followingUp, setFollowingUp] = useState(null);
  const [runningFollowups, setRunningFollowups] = useState(false);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setRefreshing(true);
      try {
        const ax = authedAxios();
        const [list, st] = await Promise.all([
          ax.get("/admin/contact?limit=300"),
          ax.get("/admin/contact/stats"),
        ]);
        setItems(list.data || []);
        setStats(st.data || null);
      } catch (err) {
        toast.error(formatApiErrorDetail(err));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authedAxios],
  );

  useEffect(() => {
    queueMicrotask(() => load());
  }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      const ax = authedAxios();
      const { data } = await ax.patch(`/admin/contact/${id}`, { status: newStatus });
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...data } : x)));
      const st = await ax.get("/admin/contact/stats");
      setStats(st.data);
      toast.success(lang === "fr" ? "Statut mis à jour" : "Status updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  const saveNote = async (id) => {
    const value = notesDraft[id] ?? "";
    setSavingNote(id);
    try {
      const ax = authedAxios();
      const { data } = await ax.patch(`/admin/contact/${id}`, { internal_notes: value });
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...data } : x)));
      setNotesDraft((d) => {
        const next = { ...d };
        delete next[id];
        return next;
      });
      toast.success(lang === "fr" ? "Note enregistrée" : "Note saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSavingNote(null);
    }
  };

  const sendFollowup = async (id) => {
    setFollowingUp(id);
    try {
      const ax = authedAxios();
      await ax.post(`/admin/contact/${id}/send-followup`);
      const { data } = await ax.get("/admin/contact?limit=300");
      setItems(data || []);
      toast.success(lang === "fr" ? "Email de relance envoyé" : "Follow-up email sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setFollowingUp(null);
    }
  };

  const runAllFollowups = async () => {
    setRunningFollowups(true);
    try {
      const ax = authedAxios();
      const { data } = await ax.post("/admin/contact/run-followups");
      const count = (data?.sent || []).length;
      if (count === 0) {
        toast.info(lang === "fr" ? "Aucune relance à envoyer." : "No follow-ups pending.");
      } else {
        toast.success(
          lang === "fr"
            ? `${count} relance${count > 1 ? "s" : ""} envoyée${count > 1 ? "s" : ""}`
            : `${count} follow-up${count > 1 ? "s" : ""} sent`,
        );
      }
      await load(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setRunningFollowups(false);
    }
  };

  const remove = async (id) => {
    if (
      !window.confirm(
        lang === "fr" ? "Supprimer cette demande ?" : "Delete this request?",
      )
    )
      return;
    try {
      const ax = authedAxios();
      await ax.delete(`/admin/contact/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      const st = await ax.get("/admin/contact/stats");
      setStats(st.data);
      toast.success(lang === "fr" ? "Demande supprimée" : "Request deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    }
  };

  const filtered =
    filter === "all" ? items : items.filter((x) => x.status === filter);

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-[#A19E9C] py-20">
        <Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-[0.3em]">
          {lang === "fr" ? "Chargement…" : "Loading…"}
        </span>
      </div>
    );
  }

  return (
    <section data-testid="admin-contact-panel" className="space-y-8">
      <PipelineStats
        stats={stats}
        lang={lang}
        onFilter={setFilter}
        runningFollowups={runningFollowups}
        onRunFollowups={runAllFollowups}
      />

      <StatusFilter
        filter={filter}
        onChange={setFilter}
        items={items}
        refreshing={refreshing}
        onRefresh={() => load()}
        lang={lang}
      />

      {filtered.length === 0 ? (
        <div className="border border-[#F2EBE5]/10 p-12 text-center">
          <p className="font-serif-display text-2xl text-[#A19E9C]">
            {lang === "fr" ? "Aucune demande." : "No requests."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#F2EBE5]/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-[#A19E9C] border-b border-[#F2EBE5]/10">
                <th className="text-left px-4 py-4">Date</th>
                <th className="text-left px-4 py-4">{lang === "fr" ? "Nom" : "Name"}</th>
                <th className="text-left px-4 py-4">Service</th>
                <th className="text-left px-4 py-4">Contact</th>
                <th className="text-left px-4 py-4">Status</th>
                <th className="text-right px-4 py-4">—</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((req) => (
                <ContactRow
                  key={req.id}
                  req={req}
                  lang={lang}
                  t={t}
                  expanded={expanded === req.id}
                  onToggleExpand={(id) => setExpanded(expanded === id ? null : id)}
                  onStatusChange={updateStatus}
                  onDelete={remove}
                  onSendFollowup={sendFollowup}
                  followingUp={followingUp}
                  notesDraft={notesDraft}
                  setNotesDraft={setNotesDraft}
                  savingNote={savingNote}
                  onSaveNote={saveNote}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default ContactRequestsPanel;
